import { useState, createContext, useContext } from "react";
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Grid,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Button,
    Tooltip,
} from "@mui/material";
import { useRecords } from "../../../../api/useRecords";
import { FrequencyBand, SelectedFilterState } from "../types";
import { BANDS_IDS, isInvalidBandSelection } from "../utils/uiUtils";

export const DataFilterContext = createContext<
    | {
          selectedFilter: SelectedFilterState;
          setSelectedFilter: (filter: SelectedFilterState) => void;
      }
    | undefined
>(undefined);

export const useDataFilter = () => {
    const context = useContext(DataFilterContext);
    if (context === undefined) {
        throw new Error("useDataFilter must be used within a DataFilterProvider");
    }
    return context;
};

type DataFilterProps = {
    selectedFilter: SelectedFilterState;
    setSelectedFilter: (filter: SelectedFilterState) => void;
};

export const DataFilter = ({ selectedFilter, setSelectedFilter }: DataFilterProps) => {
    const [tempFilter, setTempFilter] = useState<SelectedFilterState>({
        band: selectedFilter.band,
        individual: selectedFilter.individual,
    });

    const { data: bandsData } = useRecords("frequency_bands", "id,name,min_frequency", {
        extraOptions: {
            pageSize: -1,
            sort: "order",
            filter: {
                id: {
                    _in: [
                        BANDS_IDS.delta,
                        BANDS_IDS.theta,
                        BANDS_IDS.alpha,
                        BANDS_IDS.beta,
                        BANDS_IDS.highBeta,
                        BANDS_IDS.alpha1,
                        BANDS_IDS.alpha2,
                        BANDS_IDS.beta1,
                        BANDS_IDS.beta2,
                        BANDS_IDS.beta3,
                    ],
                },
            },
        },
        staleTime: Infinity,
    });

    const bands = bandsData?.items || ([] as Array<FrequencyBand>);

    const { data: individualFrequenciesData } = useRecords("frequency_individual_values", "id,name_internal,frequency", {
        extraOptions: { pageSize: -1, sort: "frequency", filter: { frequency: { _lte: 30 } } },
        staleTime: Infinity,
    });
    const individualFrequencies = individualFrequenciesData?.items.map((i: any) => ({ id: i.id, name: i.name_internal })) || [];

    const handleFilterChange = (type: keyof SelectedFilterState, value: string | Array<string>) => {
        const newFilterState: SelectedFilterState = {
            band: type === "band" ? (Array.isArray(value) ? value : value.split(",")) : tempFilter.band,
            individual: type === "individual" ? (Array.isArray(value) ? value : value.split(",")) : tempFilter.individual,
        };
        setTempFilter(newFilterState);
    };

    const handleSelectionChange = (type: keyof SelectedFilterState, value: Array<string>) => {
        const options = type === "band" ? bands : individualFrequencies;
        const allIds = options.map((opt: any) => String(opt.id));
        const isSelectAll = value.includes("select_all");

        if (isSelectAll) {
            if (tempFilter[type].length === allIds.length) {
                handleFilterChange(type, []);
            } else {
                handleFilterChange(type, allIds);
            }
        } else {
            handleFilterChange(type, value);
        }
    };

    const applyFilter = () => {
        setSelectedFilter(tempFilter);
    };

    const getActiveFilterType = () => {
        if (tempFilter.band.length > 0) return "band";
        if (tempFilter.individual.length > 0) return "individual";
        return "all";
    };

    const activeFilterType = getActiveFilterType();

    const getSelectedNames = (selected: Array<string | number>, options: Array<any>) => {
        if (selected.length === 0) return <em>Show All</em>;
        const selectedNames = selected
            .map((id) => {
                const option = options.find((o: any) => o.id === Number(id));
                return option ? option.name : "";
            })
            .filter((name) => name !== "");
        return selectedNames.join(", ");
    };

    return (
        <Box>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                    <FormControl fullWidth disabled={activeFilterType !== "band" && activeFilterType !== "all"}>
                        <InputLabel>Bandas</InputLabel>
                        <Select
                            multiple
                            value={tempFilter.band}
                            onChange={(e) => handleSelectionChange("band", e.target.value as Array<string>)}
                            input={<OutlinedInput label="Frequency Band" />}
                            renderValue={(selected: Array<string | number>) => getSelectedNames(selected, bands)}
                        >
                            <MenuItem value="select_all">
                                <Checkbox
                                    checked={bands.length > 0 && tempFilter.band.length === bands.length}
                                    indeterminate={tempFilter.band.length > 0 && tempFilter.band.length < bands.length}
                                />
                                <ListItemText primary="Selecionar todos" />
                            </MenuItem>
                            {bands.map((band: any) => (
                                <MenuItem key={band.id} value={String(band.id)}>
                                    <Checkbox checked={tempFilter.band.includes(String(band.id))} />
                                    <ListItemText primary={band.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={5}>
                    <FormControl fullWidth disabled={activeFilterType !== "individual" && activeFilterType !== "all"}>
                        <InputLabel>Frequência Individual (por Hz)</InputLabel>
                        <Select
                            multiple
                            value={tempFilter.individual}
                            onChange={(e) => handleSelectionChange("individual", e.target.value as Array<string>)}
                            input={<OutlinedInput label="Individual Frequency (per Hz)" />}
                            renderValue={(selected: Array<string | number>) => getSelectedNames(selected, individualFrequencies)}
                        >
                            <MenuItem value="select_all">
                                <Checkbox
                                    checked={
                                        individualFrequencies.length > 0 &&
                                        tempFilter.individual.length === individualFrequencies.length
                                    }
                                    indeterminate={
                                        tempFilter.individual.length > 0 &&
                                        tempFilter.individual.length < individualFrequencies.length
                                    }
                                />
                                <ListItemText primary="Selecionar todos" />
                            </MenuItem>
                            {individualFrequencies.map((freq: any) => (
                                <MenuItem key={freq.id} value={String(freq.id)}>
                                    <Checkbox checked={tempFilter.individual.includes(String(freq.id))} />
                                    <ListItemText primary={freq.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                    <Tooltip title={isInvalidBandSelection(tempFilter)} arrow>
                        <span style={{ width: "100%" }}>
                            <Button
                                variant="contained"
                                onClick={applyFilter}
                                fullWidth
                                disabled={isInvalidBandSelection(tempFilter) !== false}
                            >
                                Filtrar
                            </Button>
                        </span>
                    </Tooltip>
                </Grid>
            </Grid>
        </Box>
    );
};
